import { useState } from 'react';
import { Plus, ChevronDown, ChevronRight, Trash2, Download, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  useSubmittals,
  useCreateSubmittal,
  useDeleteSubmittal,
  useUpdateSubmittalStatus,
} from '@/hooks/useSubmittals';
import { api } from '@/lib/api';
import type { SubmittalStatus } from '@/hooks/useSubmittals';
import { SubmittalStatusBadge } from './SubmittalStatusBadge';
import { SubmittalDetailPanel } from './SubmittalDetailPanel';

interface SubmittalsSectionProps {
  projectId: string;
}

const STATUS_OPTIONS: { value: SubmittalStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'approved_as_noted', label: 'Approved as Noted' },
  { value: 'revise_resubmit', label: 'Revise & Resubmit' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'closed', label: 'Closed' },
];

export function SubmittalsSection({ projectId }: SubmittalsSectionProps) {
  const { data: submittals = [], isLoading } = useSubmittals(projectId);
  const createSubmittal = useCreateSubmittal();
  const deleteSubmittal = useDeleteSubmittal();
  const updateStatus = useUpdateSubmittalStatus();

  const [isAdding, setIsAdding] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!newTitle.trim()) return;
    await createSubmittal.mutateAsync({
      projectId,
      submittal: { title: newTitle.trim() },
    });
    setNewTitle('');
    setIsAdding(false);
  };

  const handleStatusChange = (submittalId: string, newStatus: SubmittalStatus) => {
    updateStatus.mutate({ id: submittalId, status: newStatus });
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await deleteSubmittal.mutateAsync(deleteId);
    setDeleteId(null);
    if (expandedId === deleteId) setExpandedId(null);
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground py-4">Loading submittals...</div>;
  }

  return (
    <div className="space-y-3">
      {/* Empty state */}
      {submittals.length === 0 && !isAdding && (
        <EmptyState
          icon={Package}
          title="No submittals yet"
          description="Track shop drawings, material samples, and product data"
          actionLabel="Create Submittal"
          onAction={() => setIsAdding(true)}
        />
      )}

      {/* Summary bar */}
      {submittals.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span>{submittals.length} submittal{submittals.length !== 1 ? 's' : ''}</span>
            {(() => {
              const byStatus: Record<string, number> = {};
              submittals.forEach(s => { byStatus[s.status] = (byStatus[s.status] || 0) + 1; });
              return Object.entries(byStatus).map(([status, count]) => (
                <span key={status} className="flex items-center gap-1">
                  <SubmittalStatusBadge status={status as SubmittalStatus} className="text-[10px] px-1.5 py-0" />
                  <span className="text-xs">{count}</span>
                </span>
              ));
            })()}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => api.submittals.exportLog(projectId, 'csv')}
              title="Export CSV"
            >
              <Download className="w-4 h-4 mr-1" />
              CSV
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => api.submittals.exportLog(projectId, 'pdf')}
              title="Export PDF"
            >
              <Download className="w-4 h-4 mr-1" />
              PDF
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAdding(true)}
            >
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      {submittals.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-xs text-muted-foreground">
                <th className="px-3 py-2 text-left w-8"></th>
                <th className="px-3 py-2 text-left w-24">#</th>
                <th className="px-3 py-2 text-left">Title</th>
                <th className="px-3 py-2 text-left w-28">Spec</th>
                <th className="px-3 py-2 text-left w-32">To</th>
                <th className="px-3 py-2 text-left w-36">Status</th>
                <th className="px-3 py-2 text-center w-12">Rev</th>
                <th className="px-3 py-2 text-left w-24">Required</th>
                <th className="px-3 py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {submittals.map((sub) => (
                <SubmittalRow
                  key={sub.id}
                  submittal={sub}
                  isExpanded={expandedId === sub.id}
                  onToggle={() => setExpandedId(expandedId === sub.id ? null : sub.id)}
                  onStatusChange={handleStatusChange}
                  onDelete={() => setDeleteId(sub.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add form */}
      {isAdding && (
        <div className="border border-dashed border-blue-300 rounded-lg p-3">
          <div className="flex gap-2">
            <Input
              placeholder="Submittal title..."
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="h-8 text-sm"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreate();
                if (e.key === 'Escape') setIsAdding(false);
              }}
            />
            <Button
              size="sm"
              className="h-8"
              onClick={handleCreate}
              disabled={!newTitle.trim() || createSubmittal.isPending}
            >
              Add
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8"
              onClick={() => { setIsAdding(false); setNewTitle(''); }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Submittal</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the submittal and all its items, files, and revision history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ============================================================================
// SubmittalRow — extracted for cleanliness
// ============================================================================

interface SubmittalRowProps {
  submittal: ReturnType<typeof useSubmittals>['data'] extends (infer T)[] | undefined ? T : never;
  isExpanded: boolean;
  onToggle: () => void;
  onStatusChange: (id: string, status: SubmittalStatus) => void;
  onDelete: () => void;
}

function SubmittalRow({ submittal, isExpanded, onToggle, onStatusChange, onDelete }: SubmittalRowProps) {
  return (
    <>
      <tr className="border-t hover:bg-muted/30 group cursor-pointer" onClick={onToggle}>
        <td className="px-3 py-2">
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          )}
        </td>
        <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
          {submittal.submittal_number}
        </td>
        <td className="px-3 py-2 font-medium">
          {submittal.title}
          <span className="text-xs text-muted-foreground ml-2">
            {submittal.item_count > 0 && `${submittal.item_count} item${submittal.item_count !== 1 ? 's' : ''}`}
            {submittal.item_count > 0 && submittal.file_count > 0 && ' · '}
            {submittal.file_count > 0 && `${submittal.file_count} file${submittal.file_count !== 1 ? 's' : ''}`}
          </span>
        </td>
        <td className="px-3 py-2 text-muted-foreground text-xs">
          {submittal.spec_section || '—'}
        </td>
        <td className="px-3 py-2 text-muted-foreground text-xs truncate max-w-[128px]">
          {submittal.submitted_to || '—'}
        </td>
        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
          <Select
            value={submittal.status}
            onValueChange={(val) => onStatusChange(submittal.id, val as SubmittalStatus)}
          >
            <SelectTrigger className="h-7 w-auto border-none p-0 shadow-none">
              <SelectValue>
                <SubmittalStatusBadge status={submittal.status} />
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </td>
        <td className="px-3 py-2 text-center">
          <span className="bg-primary/10 text-primary/70 rounded px-1.5 py-0.5 text-xs font-medium">
            {submittal.current_revision}
          </span>
        </td>
        <td className="px-3 py-2 text-xs text-muted-foreground">
          {submittal.date_required
            ? new Date(submittal.date_required).toLocaleDateString()
            : '—'
          }
        </td>
        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
          <button
            className="p-1 opacity-0 group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive rounded"
            onClick={onDelete}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={9} className="p-0">
            <SubmittalDetailPanel submittalId={submittal.id} />
          </td>
        </tr>
      )}
    </>
  );
}
